import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import AppContainer from "../../Components/Accounting/AppContainer";
import DataTable from "../../Components/Accounting/DataTable";
import Select2 from "../../Components/Accounting/Select2";
import { CardSkeleton } from "../../Components/Accounting/SkeletonLoader";
import ajaxAccounting from "../../util/accounting/remote/ajaxAccounting";
import functions from "../../util/accounting/functions";
import toast, { Toaster } from "react-hot-toast";

const InvoiceAging = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const defaultType = searchParams.get("type") || "outgoing";
  const [financialYears, setFinancialYears] = useState([]);
  const [selectedFY, setSelectedFY] = useState("");
  const [invoiceType, setInvoiceType] = useState(defaultType);
  const [agingData, setAgingData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { fetchFinancialYears(); }, []);
  useEffect(() => { if (selectedFY) fetchAging(); }, [selectedFY, invoiceType]);

  const fetchFinancialYears = async () => {
    const res = await ajaxAccounting.listFinancialYears({});
    if (res.status === "OK") {
      setFinancialYears(res.details || []);
      const cur = (res.details || []).find((y) => y.is_current === 1);
      setSelectedFY(String(cur?.id || res.details?.[0]?.id || ""));
    }
  };

  const fetchAging = async () => {
    setLoading(true);
    const res = await ajaxAccounting.getInvoiceAging({ financial_year_id: parseInt(selectedFY), type: invoiceType });
    if (res.status === "OK") setAgingData(res.details);
    else toast.error(res.message || "Failed to fetch aging");
    setLoading(false);
  };

  const agingBuckets = [
    { key: "current", label: "Current (Not Due)", color: "#10b981" },
    { key: "overdue_1_30", label: "1-30 Days", color: "#f59e0b" },
    { key: "overdue_31_60", label: "31-60 Days", color: "#ef4444" },
    { key: "overdue_61_90", label: "61-90 Days", color: "#dc2626" },
    { key: "overdue_90_plus", label: "90+ Days", color: "#7f1d1d" },
  ];

  const invoices = agingData?.invoices || [];

  const getAgingBucket = (invoice) => {
    const days = Math.floor((new Date() - new Date(invoice.due_date)) / (1000 * 60 * 60 * 24));
    if (days <= 0) return "current";
    if (days <= 30) return "overdue_1_30";
    if (days <= 60) return "overdue_31_60";
    if (days <= 90) return "overdue_61_90";
    return "overdue_90_plus";
  };

  const bucketTotals = agingBuckets.reduce((acc, b) => {
    acc[b.key] = invoices.filter((i) => getAgingBucket(i) === b.key).reduce((s, i) => s + (parseFloat(i.balance_due) || 0), 0);
    return acc;
  }, {});

  const columns = [
    { key: "invoice_number", label: "Invoice #", render: (v, row) => <Link to={`/accounting/invoices/${row.id}`} className="tx-primary fw-bold">{v || `INV-${row.id}`}</Link> },
    { key: "contact_name", label: invoiceType === "outgoing" ? "Customer" : "Supplier" },
    { key: "invoice_date", label: "Invoice Date", render: (v) => functions.formatDate(v) },
    { key: "due_date", label: "Due Date", render: (v) => functions.formatDate(v) },
    { key: "_days", label: "Days Overdue", render: (_, row) => {
      const days = Math.floor((new Date() - new Date(row.due_date)) / (1000 * 60 * 60 * 24));
      return days > 0 ? <span className="tx-danger fw-bold">{days} days</span> : <span className="tx-success">Not due</span>;
    }},
    { key: "total_amount", label: "Invoice Total", render: (v) => functions.formatCurrency(v) },
    { key: "amount_paid", label: "Paid", render: (v) => <span className="tx-success">{functions.formatCurrency(v)}</span> },
    { key: "balance_due", label: "Balance Due", render: (v) => <span className="tx-danger fw-bold">{functions.formatCurrency(v)}</span> },
    {
      key: "id", label: "Action",
      render: (id, row) => (
        <button className="btn btn-sm btn-primary" onClick={() => navigate(`/accounting/invoices/${id}/pay`)} title="Record Payment">
          <i className="fa-solid fa-money-bill-wave me-1"></i>Pay
        </button>
      ),
    },
  ];

  const unpaidInvoices = invoices.filter((i) => i.status !== "paid" && i.status !== "void" && (parseFloat(i.balance_due) || 0) > 0);

  return (
    <AppContainer
      title={invoiceType === "outgoing" ? "Accounts Receivable Aging" : "Accounts Payable Aging"}
      previousPageTitle="Invoices"
      description={invoiceType === "outgoing" ? "Track unpaid customer invoices by age. Older unpaid invoices need follow-up. Red entries are overdue." : "Track unpaid supplier invoices by age. Ensure timely payments to maintain good supplier relationships."}
      actions={[
        { label: "New Invoice", icon: "fa-solid fa-plus", className: "btn btn-primary", onClick: () => navigate(`/accounting/invoices/new?type=${invoiceType}`) },
        { label: "View All Invoices", icon: "fa-solid fa-list", className: "btn btn-outline-secondary", onClick: () => navigate(`/accounting/invoices?type=${invoiceType}`) },
      ]}
    >
      <Toaster />

      <div className="card custom-card p-3 mb-3">
        <div className="row g-2">
          <div className="col-lg-4">
            <label className="form-label tx-12 fw-semibold">Type</label>
            <Select2 options={[{ value: "outgoing", label: "Customer Invoices (AR)" }, { value: "incoming", label: "Supplier Invoices (AP)" }]} value={invoiceType} onChange={setInvoiceType} />
          </div>
          <div className="col-lg-4">
            <label className="form-label tx-12 fw-semibold">Financial Year</label>
            <Select2 options={financialYears.map((y) => ({ value: String(y.id), label: y.name }))} value={selectedFY} onChange={setSelectedFY} placeholder="Select Year" />
          </div>
        </div>
      </div>

      {loading ? <CardSkeleton count={5} /> : (
        <div className="row mb-3">
          {agingBuckets.map((b) => (
            <div key={b.key} className="col-xl col-lg-4 col-sm-6 mb-2">
              <div className="card custom-card p-3 text-center" style={{ borderTop: `4px solid ${b.color}` }}>
                <div className="tx-11 tx-muted mb-1">{b.label}</div>
                <div className="fw-bold" style={{ color: b.color, fontSize: "1.05rem" }}>{functions.formatCurrency(bucketTotals[b.key] || 0)}</div>
                <div className="tx-11 tx-muted">{invoices.filter((i) => getAgingBucket(i) === b.key).length} invoices</div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="card custom-card p-3">
        <div className="mb-2 d-flex justify-content-between align-items-center">
          <h6 className="fw-semibold mb-0">Unpaid Invoices</h6>
          <span className="badge bg-danger">{unpaidInvoices.length} unpaid · {functions.formatCurrency(unpaidInvoices.reduce((s, i) => s + (parseFloat(i.balance_due) || 0), 0))}</span>
        </div>
        <DataTable columns={columns} data={unpaidInvoices} loading={loading} pageSize={15} emptyMessage="No unpaid invoices. All clear!" />
      </div>
    </AppContainer>
  );
};

export default InvoiceAging;
